/**
 * PersonelDetayFotosu.jsx — firma portalındaki Personel Detay başlığının vesikalık kutusu (backlog #726).
 *
 * Kişinin GERÇEK fotoğrafını gösterir (`useKisiFoto`, GET /personnel/{id}/fotograf; yalnızca `fotografVar` iken okunur, okunamazsa kişi ikonu).
 * Kural listedeki vesikalıkla (KartKarariVesikalik) AYNI ve sessizdir:
 *   · `degistirebilir` false (kimliği SAKA'ca doğrulanmış kişi, ilişkisi bitmiş kişi, salt okunur görünüm) → düz fotoğraf: işaretçi yok,
 *     başlık yok, yükleme ipucu yok, kilit yazısı yok;
 *   · true → kutuya tıklamak dosya seçtirir, yeni fotoğraf hemen görünür.
 * Yükleme kutunun işidir: POST /personnel/{id}/fotograf. Başarıda `onBasarili(mesaj)`; hatada `onHata(mesaj, { kilitli })` — `kilitli` 403
 * KIMLIK_DOGRULANMIS (arada SAKA doğrulamış): backend metni Türkçe sabit olduğu için `kod`dan çevrilir, çağıran sayfayı tazelesin diye bildirilir.
 *
 * @param {number}   personId
 * @param {string}   ad              bildirim metnindeki kişi adı
 * @param {boolean}  fotografVar     satırın / detayın `fotografVar` bayrağı
 * @param {boolean}  degistirebilir  firma bu kişinin fotoğrafını şimdi değiştirebilir mi
 * @param {Function} onBasarili      (mesaj) => void
 * @param {Function} onHata          (mesaj, { kilitli }) => void
 */
import React, { useRef, useState } from "react";
import { Spinner } from "reactstrap";
import { User } from "lucide-react";
import { useTranslation } from "react-i18next";
import useKisiFoto from "./useKisiFoto";
import { uploadKisiFotograf } from "../../services/kisiKunyeService";

const FOTO_TURLERI = /^image\/(jpeg|png|webp)$/i;
const FOTO_UZANTILARI = /\.(jpe?g|png|webp)$/i;

export default function PersonelDetayFotosu({ personId, ad, fotografVar, degistirebilir, onBasarili, onHata }) {
  const { t } = useTranslation("components");
  const girdiRef = useRef(null);
  const [surum, setSurum] = useState(0);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [yuklendi, setYuklendi] = useState(false); // bu oturumda yüklendi → detay yeniden okunmadan da fotoğraf okunur
  const foto = useKisiFoto(personId, Boolean(fotografVar) || yuklendi, surum);

  async function dosyaSecildi(e) {
    const dosya = e.target.files?.[0];
    e.target.value = ""; // aynı dosya tekrar seçilebilsin
    if (!dosya) return;
    if (!FOTO_TURLERI.test(dosya.type) && !FOTO_UZANTILARI.test(dosya.name)) {
      onHata?.(t("personelSekmeler.fotoGecersizTip"), { kilitli: false });
      return;
    }
    setYukleniyor(true);
    try {
      await uploadKisiFotograf(personId, dosya);
      setYuklendi(true);
      setSurum((n) => n + 1);
      onBasarili?.(t("personelSekmeler.fotoGuncellendi", { ad }));
    } catch (err) {
      const kilitli = err.kod === "KIMLIK_DOGRULANMIS";
      onHata?.(
        kilitli ? t("personelSekmeler.fotoKimlikDogrulanmis") : err.status == null ? t("personelSekmeler.fotoSunucuYanitVermedi") : err.message,
        { kilitli },
      );
    } finally {
      setYukleniyor(false);
    }
  }

  const tikla = degistirebilir && !yukleniyor
    ? {
        role: "button",
        tabIndex: 0,
        title: t("personelKart.fotoYukle"),
        onClick: () => girdiRef.current?.click(),
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); girdiRef.current?.click(); }
        },
      }
    : {};

  return (
    <div
      className="bg-white rounded-3 d-flex align-items-center justify-content-center shadow-sm flex-shrink-0 position-relative overflow-hidden"
      style={{ width: 64, height: 64, ...(degistirebilir ? { cursor: "pointer" } : null) }}
      {...tikla}
    >
      {foto ? (
        <img src={foto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 12 }} />
      ) : (
        <User size={32} className="text-danger" />
      )}
      {degistirebilir && (
        <input ref={girdiRef} type="file" accept="image/jpeg,image/png,image/webp" className="d-none" onChange={dosyaSecildi} />
      )}
      {yukleniyor && (
        <span
          className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex align-items-center justify-content-center"
          style={{ pointerEvents: "none" }}
        >
          <Spinner size="sm" color="secondary" />
        </span>
      )}
    </div>
  );
}
